import React, { useState, useEffect } from 'react';
import { Card, CardContent } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Badge } from '../ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import { toast } from 'sonner';
import MayaPanel from './MayaPanel';

// ─── constants ────────────────────────────────────────────────────────────────

const CAMPAIGN_STATUS = {
  draft:     { label: 'Rascunho',   color: 'bg-gray-100 text-gray-600 border-gray-200' },
  running:   { label: 'Executando', color: 'bg-blue-100 text-blue-700 border-blue-200' },
  paused:    { label: 'Pausada',    color: 'bg-amber-100 text-amber-700 border-amber-200' },
  completed: { label: 'Concluída',  color: 'bg-green-100 text-green-700 border-green-200' },
};

const INV_STATUS = {
  pending:   { label: 'Aguardando', color: 'bg-yellow-100 text-yellow-700', dot: 'bg-yellow-400' },
  accepted:  { label: 'Aceito',     color: 'bg-green-100 text-green-700',   dot: 'bg-green-500' },
  declined:  { label: 'Recusado',   color: 'bg-red-100 text-red-600',       dot: 'bg-red-400' },
  failed:    { label: 'Falhou',     color: 'bg-red-100 text-red-600',       dot: 'bg-red-400' },
  withdrawn: { label: 'Retirado',   color: 'bg-gray-100 text-gray-500',     dot: 'bg-gray-400' },
};

const CONNECTION_DEGREES = [
  { value: '2nd',  label: '2º grau (conexões das suas conexões)' },
  { value: '3rd',  label: '3º grau' },
  { value: 'all',  label: 'Todos os graus' },
];

const INDUSTRIES = [
  'Tecnologia', 'Marketing', 'Vendas', 'Recursos Humanos', 'Finanças',
  'Consultoria', 'Saúde', 'Educação', 'Varejo', 'Manufatura',
  'Logística', 'Imobiliário', 'Jurídico', 'Mídia', 'Construção',
];

// ─── helpers ──────────────────────────────────────────────────────────────────

function StatusBadge({ status, map }) {
  const s = map[status] || { label: status, color: 'bg-gray-100 text-gray-600' };
  return (
    <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2.5 py-1 rounded-full border ${s.color}`}>
      {s.dot && <span className={`w-1.5 h-1.5 rounded-full ${s.dot}`} />}
      {s.label}
    </span>
  );
}

function StatBox({ value, label, color = 'gray' }) {
  const colors = {
    gray:   'bg-gray-50 border-gray-200 text-gray-700',
    blue:   'bg-blue-50 border-blue-200 text-blue-700',
    green:  'bg-green-50 border-green-200 text-green-700',
    amber:  'bg-amber-50 border-amber-200 text-amber-700',
  };
  return (
    <div className={`rounded-xl border px-4 py-3 text-center ${colors[color]}`}>
      <p className="text-2xl font-bold">{value ?? 0}</p>
      <p className="text-xs mt-0.5 opacity-70">{label}</p>
    </div>
  );
}

// ─── Campaign Form ────────────────────────────────────────────────────────────

function CampaignFormModal({ open, onClose, onSaved, api, editing }) {
  const empty = {
    name: '', account_id: '',
    keywords: '', title: '', company: '', location: '', school: '', industry: '',
    connection_degree: '2nd', limit: 50,
    note: '', auto_message: '',
  };
  const [form, setForm] = useState(empty);
  const [accounts, setAccounts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [synklyOk, setSynklyOk] = useState(true);

  useEffect(() => {
    if (!open) return;
    if (editing) setForm({ ...empty, ...editing });
    else setForm(empty);
    fetchAccounts();
  }, [open]);

  const fetchAccounts = async () => {
    try {
      const res = await api.get('/synkly/accounts');
      const li = (res.data || []).filter(a => a.channel === 'linkedin' && a.status === 'connected');
      setAccounts(li);
      setSynklyOk(true);
    } catch {
      setSynklyOk(false);
    }
  };

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const handleSave = async () => {
    if (!form.name.trim()) { toast.error('Informe o nome da campanha'); return; }
    if (!form.account_id) { toast.error('Selecione uma conta LinkedIn'); return; }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/linkedin-campaigns/${editing.id}`, form);
        toast.success('Campanha atualizada!');
      } else {
        await api.post('/linkedin-campaigns', form);
        toast.success('Campanha criada!');
      }
      onSaved();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao salvar campanha');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{editing ? '✏️ Editar campanha' : '🚀 Nova campanha LinkedIn'}</DialogTitle>
        </DialogHeader>

        {!synklyOk && (
          <div className="bg-amber-50 border border-amber-200 rounded-lg px-4 py-3 text-sm text-amber-800">
            ⚠️ Synkly não configurado ou sem contas LinkedIn conectadas.
            Configure em <strong>Integrações → Integrações Gerais</strong> primeiro.
          </div>
        )}

        <div className="space-y-5 mt-2">
          {/* Básico */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">Informações básicas</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Nome da campanha *</Label>
                <Input className="mt-1" placeholder="Ex: Prospecção CTOs São Paulo" value={form.name} onChange={e => set('name', e.target.value)} />
              </div>
              <div className="col-span-2">
                <Label>Conta LinkedIn (Synkly) *</Label>
                <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.account_id} onChange={e => set('account_id', e.target.value)}>
                  <option value="">Selecionar conta…</option>
                  {accounts.map(a => <option key={a.id} value={a.id}>{a.label}</option>)}
                </select>
                {accounts.length === 0 && synklyOk && <p className="text-xs text-red-500 mt-1">Nenhuma conta LinkedIn conectada. Acesse Integrações Gerais → LinkedIn.</p>}
              </div>
            </div>
          </section>

          {/* Filtros de busca */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">🔍 Filtros de pesquisa de perfis</h3>
            <div className="grid grid-cols-2 gap-3">
              <div className="col-span-2">
                <Label>Palavras-chave</Label>
                <Input className="mt-1" placeholder="Ex: growth hacker, head de vendas" value={form.keywords} onChange={e => set('keywords', e.target.value)} />
                <p className="text-xs text-gray-400 mt-1">Busca em nome, cargo e empresa do perfil</p>
              </div>
              <div>
                <Label>Cargo (title)</Label>
                <Input className="mt-1" placeholder="Ex: CTO, Diretor Comercial" value={form.title} onChange={e => set('title', e.target.value)} />
              </div>
              <div>
                <Label>Empresa</Label>
                <Input className="mt-1" placeholder="Ex: Itaú, Ambev" value={form.company} onChange={e => set('company', e.target.value)} />
              </div>
              <div>
                <Label>Localização</Label>
                <Input className="mt-1" placeholder="Ex: São Paulo, Brasil" value={form.location} onChange={e => set('location', e.target.value)} />
              </div>
              <div>
                <Label>Escola / Universidade</Label>
                <Input className="mt-1" placeholder="Ex: FGV, USP" value={form.school} onChange={e => set('school', e.target.value)} />
              </div>
              <div>
                <Label>Setor / Indústria</Label>
                <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.industry} onChange={e => set('industry', e.target.value)}>
                  <option value="">Qualquer setor</option>
                  {INDUSTRIES.map(i => <option key={i} value={i}>{i}</option>)}
                </select>
              </div>
              <div>
                <Label>Grau de conexão</Label>
                <select className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm" value={form.connection_degree} onChange={e => set('connection_degree', e.target.value)}>
                  {CONNECTION_DEGREES.map(d => <option key={d.value} value={d.value}>{d.label}</option>)}
                </select>
              </div>
              <div>
                <Label>Máx. perfis a buscar</Label>
                <Input type="number" min="5" max="500" className="mt-1" value={form.limit} onChange={e => set('limit', Number(e.target.value))} />
                <p className="text-xs text-gray-400 mt-1">Limite do Synkly por execução</p>
              </div>
            </div>
          </section>

          {/* Convite */}
          <section>
            <h3 className="text-xs font-bold text-gray-400 uppercase tracking-wider mb-3">✉️ Configurações de convite</h3>
            <div className="space-y-3">
              <div>
                <Label>Nota do convite <span className="text-gray-400 font-normal">(opcional, máx. 300 chars)</span></Label>
                <textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-20" maxLength={300}
                  placeholder="Olá {nome}, vi seu perfil e gostaria de conectar…"
                  value={form.note} onChange={e => set('note', e.target.value)} />
                <p className="text-xs text-gray-400 text-right">{form.note.length}/300</p>
              </div>
              <div>
                <Label>Mensagem automática ao aceitar</Label>
                <textarea className="mt-1 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm h-20"
                  placeholder="Obrigado por conectar, {nome}! Queria apresentar…"
                  value={form.auto_message} onChange={e => set('auto_message', e.target.value)} />
                <p className="text-xs text-blue-500 mt-1">💡 Enviada automaticamente via Synkly quando o convite for aceito</p>
              </div>
            </div>
          </section>

          <div className="flex justify-end gap-2 pt-3 border-t">
            <Button variant="outline" onClick={onClose}>Cancelar</Button>
            <Button onClick={handleSave} disabled={saving}>{saving ? 'Salvando…' : editing ? 'Salvar alterações' : 'Criar campanha'}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Campaign Card ────────────────────────────────────────────────────────────

function CampaignCard({ campaign, onOpen, onRun, onPause, onDelete, onEdit, running }) {
  const s = CAMPAIGN_STATUS[campaign.status] || CAMPAIGN_STATUS.draft;
  return (
    <Card className="border border-gray-200 hover:border-blue-300 transition-all cursor-pointer group" onClick={() => onOpen(campaign)}>
      <CardContent className="p-5">
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="font-semibold text-gray-900 group-hover:text-blue-700 transition-colors">{campaign.name}</h3>
              <span className={`text-xs font-semibold px-2 py-0.5 rounded-full border ${s.color}`}>{s.label}</span>
            </div>
            <div className="flex flex-wrap gap-2 mt-2">
              {campaign.keywords && <Tag>🔎 {campaign.keywords}</Tag>}
              {campaign.title && <Tag>💼 {campaign.title}</Tag>}
              {campaign.location && <Tag>📍 {campaign.location}</Tag>}
              {campaign.company && <Tag>🏢 {campaign.company}</Tag>}
              {campaign.industry && <Tag>🏭 {campaign.industry}</Tag>}
            </div>
          </div>
          <div className="flex gap-1.5 ml-4 shrink-0" onClick={e => e.stopPropagation()}>
            {campaign.status !== 'running' ? (
              <Button size="sm" onClick={() => onRun(campaign)} disabled={running === campaign.id} className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3">
                {running === campaign.id ? '⏳' : '▶ Executar'}
              </Button>
            ) : (
              <Button size="sm" variant="outline" onClick={() => onPause(campaign)} className="text-amber-600 border-amber-300 text-xs px-3">⏸ Pausar</Button>
            )}
            <Button size="sm" variant="ghost" onClick={() => onEdit(campaign)} className="text-gray-400 hover:text-gray-700">✏️</Button>
            <Button size="sm" variant="ghost" onClick={() => onDelete(campaign)} className="text-gray-400 hover:text-red-500">🗑️</Button>
          </div>
        </div>

        <div className="grid grid-cols-4 gap-2 mt-3">
          <MiniStat label="Encontrados" value={campaign.total_found} />
          <MiniStat label="Convidados" value={campaign.total_invited} />
          <MiniStat label="Aceitos" value={campaign.total_accepted} />
        </div>

        <div className="flex items-center justify-between mt-4 pt-3 border-t border-gray-100">
          {campaign.last_run_at ? (
            <p className="text-xs text-gray-400">
              Última execução: {new Date(campaign.last_run_at).toLocaleString('pt-BR')}
            </p>
          ) : <span />}
          <div className="flex items-center gap-2" onClick={e => e.stopPropagation()}>
            <span className="text-xs text-purple-600 bg-purple-50 border border-purple-200 px-2 py-0.5 rounded-full font-medium">🤖 Maya SDR</span>
            <Button size="sm" onClick={() => onOpen(campaign)} className="bg-blue-600 hover:bg-blue-700 text-white text-xs px-3">
              Abrir campanha →
            </Button>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

function Tag({ children }) {
  return <span className="inline-flex items-center bg-gray-100 text-gray-600 text-xs px-2 py-0.5 rounded-full">{children}</span>;
}

function MiniStat({ label, value }) {
  return (
    <div className="bg-gray-50 rounded-lg px-2 py-1.5 text-center">
      <p className="text-base font-bold text-gray-800">{value ?? 0}</p>
      <p className="text-xs text-gray-500 leading-tight">{label}</p>
    </div>
  );
}

// ─── Run Result Modal ────────────────────────────────────────────────────────

function RunResultModal({ result, open, onClose }) {
  if (!result) return null;
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>📊 Resultado da execução</DialogTitle></DialogHeader>
        <div className="space-y-4 mt-2">
          <div className="grid grid-cols-2 gap-3">
            <StatBox label="Perfis encontrados" value={result.profiles_found} color="blue" />
            <StatBox label="Convites enviados" value={result.invited} color="green" />
            <StatBox label="Já convidados (skip)" value={result.skipped} color="gray" />
          </div>
          {result.search_error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-700">
              <strong>Erro na busca:</strong> {result.search_error}
            </div>
          )}
          {result.errors?.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-sm text-amber-700">
              <strong>{result.errors.length} erros no envio</strong>
              <ul className="mt-1 space-y-0.5 text-xs">
                {result.errors.slice(0, 3).map((e, i) => <li key={i} className="font-mono truncate">• {e.error}</li>)}
                {result.errors.length > 3 && <li>…e mais {result.errors.length - 3}</li>}
              </ul>
            </div>
          )}
          {result.profiles_found === 0 && !result.search_error && (
            <p className="text-sm text-gray-500 text-center">Nenhum perfil encontrado com esses filtros. Tente ampliar os critérios de busca.</p>
          )}
          <Button className="w-full" onClick={onClose}>Fechar</Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// ─── Invitations Panel ────────────────────────────────────────────────────────

function InvitationsPanel({ campaign, api, onBack, hideHeader }) {
  const [invitations, setInvitations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [runResult, setRunResult] = useState(null);
  const [runResultOpen, setRunResultOpen] = useState(false);
  const [running, setRunning] = useState(false);

  const fetchInvitations = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/linkedin-campaigns/${campaign.id}/invitations`);
      setInvitations(res.data || []);
    } catch { toast.error('Erro ao carregar convites'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchInvitations(); }, [campaign.id]);

  const handleRun = async () => {
    setRunning(true);
    try {
      const res = await api.post(`/linkedin-campaigns/${campaign.id}/run`);
      setRunResult(res.data);
      setRunResultOpen(true);
      fetchInvitations();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao executar campanha');
    } finally { setRunning(false); }
  };

  const filtered = invitations.filter(inv => {
    const matchStatus = filter === 'all' || inv.status === filter;
    const matchSearch = !search || (inv.profile_name || '').toLowerCase().includes(search.toLowerCase()) || (inv.profile_headline || '').toLowerCase().includes(search.toLowerCase());
    return matchStatus && matchSearch;
  });

  const counts = {
    total: invitations.length,
    pending: invitations.filter(i => i.status === 'pending').length,
    accepted: invitations.filter(i => i.status === 'accepted').length,
    declined: invitations.filter(i => i.status === 'declined').length,
  };

  return (
    <div className="space-y-5">
      {/* Header */}
      {!hideHeader && (
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 transition-colors text-lg">←</button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{campaign.name}</h2>
          <p className="text-sm text-gray-500">Campanha de prospecção LinkedIn</p>
        </div>
        <div className="ml-auto flex gap-2">
          <Button variant="outline" size="sm" onClick={fetchInvitations}>🔄 Atualizar</Button>
          <Button size="sm" onClick={handleRun} disabled={running} className="bg-blue-600 hover:bg-blue-700 text-white">
            {running ? '⏳ Executando…' : '▶ Executar campanha'}
          </Button>
        </div>
      </div>
      )}
      {hideHeader && (
        <div className="flex justify-end gap-2">
          <Button variant="outline" size="sm" onClick={fetchInvitations}>🔄 Atualizar</Button>
          <Button size="sm" onClick={handleRun} disabled={running} className="bg-blue-600 hover:bg-blue-700 text-white">
            {running ? '⏳ Executando…' : '▶ Executar campanha'}
          </Button>
        </div>
      )}

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Total encontrados" value={campaign.total_found} color="gray" />
        <StatBox label="Convidados" value={counts.total} color="blue" />
        <StatBox label="Aceitos" value={counts.accepted} color="green" />
        <StatBox label="Aguardando" value={counts.pending} color="amber" />
      </div>

      {/* Campaign filter info */}
      <div className="bg-blue-50 border border-blue-200 rounded-xl p-4">
        <p className="text-xs font-bold text-blue-900 mb-2">🔍 Filtros da campanha</p>
        <div className="flex flex-wrap gap-2">
          {campaign.keywords && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">🔎 {campaign.keywords}</span>}
          {campaign.title && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">💼 {campaign.title}</span>}
          {campaign.location && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">📍 {campaign.location}</span>}
          {campaign.company && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">🏢 {campaign.company}</span>}
          {campaign.school && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">🎓 {campaign.school}</span>}
          {campaign.industry && <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">🏭 {campaign.industry}</span>}
          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">👥 {campaign.connection_degree}º grau</span>
          <span className="bg-blue-100 text-blue-700 text-xs px-2 py-0.5 rounded-full">📊 máx. {campaign.limit} perfis</span>
        </div>
        {campaign.auto_message && (
          <p className="text-xs text-blue-600 mt-2 border-t border-blue-200 pt-2">
            💬 Msg automática ao aceitar: "<span className="italic">{campaign.auto_message.slice(0, 80)}{campaign.auto_message.length > 80 ? '…' : ''}</span>"
          </p>
        )}
      </div>

      {/* Invitations list */}
      <div className="space-y-3">
        <div className="flex items-center gap-3 flex-wrap">
          <Input className="w-56 text-sm" placeholder="🔍 Buscar por nome…" value={search} onChange={e => setSearch(e.target.value)} />
          <div className="flex gap-1">
            {[['all', 'Todos', counts.total], ['pending', 'Aguardando', counts.pending], ['accepted', 'Aceitos', counts.accepted], ['declined', 'Recusados', counts.declined]].map(([v, l, c]) => (
              <button key={v} onClick={() => setFilter(v)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === v ? 'bg-blue-600 text-white' : 'bg-gray-100 text-gray-600 hover:bg-gray-200'}`}>
                {l} <span className={`ml-1 px-1.5 py-0.5 rounded-full text-xs ${filter === v ? 'bg-blue-500' : 'bg-gray-200'}`}>{c}</span>
              </button>
            ))}
          </div>
        </div>

        {loading ? (
          <div className="text-center py-10 text-gray-400"><span className="animate-spin inline-block mr-2">⏳</span>Carregando convites…</div>
        ) : filtered.length === 0 ? (
          <Card><CardContent className="p-10 text-center text-gray-400">
            <p className="text-3xl mb-2">👥</p>
            <p className="font-medium">{invitations.length === 0 ? 'Nenhum convite enviado ainda' : 'Nenhum resultado para este filtro'}</p>
            {invitations.length === 0 && <p className="text-sm mt-1">Clique em "▶ Executar campanha" para buscar perfis e enviar convites</p>}
          </CardContent></Card>
        ) : (
          <div className="space-y-2">
            {filtered.map(inv => (
              <Card key={inv.id} className="border border-gray-200 hover:border-blue-200 transition-colors">
                <CardContent className="p-4 flex items-center gap-4">
                  {/* Avatar */}
                  <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-500 to-blue-700 flex items-center justify-center text-white font-bold text-sm shrink-0">
                    {(inv.profile_name || '?').charAt(0).toUpperCase()}
                  </div>
                  {/* Info */}
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <p className="font-semibold text-gray-900 text-sm truncate">{inv.profile_name || 'Nome não disponível'}</p>
                      {inv.profile_url && (
                        <a href={inv.profile_url} target="_blank" rel="noreferrer"
                          className="text-blue-500 hover:text-blue-700 text-xs shrink-0">🔗 Ver perfil</a>
                      )}
                    </div>
                    {inv.profile_headline && <p className="text-xs text-gray-500 truncate mt-0.5">{inv.profile_headline}</p>}
                    {inv.profile_location && <p className="text-xs text-gray-400">📍 {inv.profile_location}</p>}
                  </div>
                  {/* Status + dates */}
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <StatusBadge status={inv.status} map={INV_STATUS} />
                    <p className="text-xs text-gray-400">
                      Enviado {new Date(inv.invited_at).toLocaleDateString('pt-BR')}
                    </p>
                    {inv.accepted_at && (
                      <p className="text-xs text-green-600">
                        Aceito {new Date(inv.accepted_at).toLocaleDateString('pt-BR')}
                      </p>
                    )}
                    {inv.message_sent && (
                      <span className="text-xs text-blue-500 font-medium">💬 Msg enviada</span>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>

      <RunResultModal result={runResult} open={runResultOpen} onClose={() => setRunResultOpen(false)} />
    </div>
  );
}

// ─── Campaign Detail (tabs: Convites + Maya) ──────────────────────────────────

function CampaignDetail({ campaign, api, onBack }) {
  const [activeTab, setActiveTab] = useState('invitations');
  const TABS = [
    { id: 'invitations', label: '📋 Convites' },
    { id: 'maya',        label: '🤖 Maya SDR' },
  ];
  return (
    <div className="space-y-4">
      {/* Back + name */}
      <div className="flex items-center gap-3">
        <button onClick={onBack} className="text-gray-400 hover:text-gray-700 transition-colors text-lg">←</button>
        <div>
          <h2 className="text-xl font-bold text-gray-900">{campaign.name}</h2>
          <p className="text-sm text-gray-500">Campanha de prospecção LinkedIn</p>
        </div>
      </div>
      {/* Tab bar */}
      <div className="flex gap-1 bg-gray-100 p-1 rounded-xl w-fit">
        {TABS.map(tab => (
          <button key={tab.id} onClick={() => setActiveTab(tab.id)}
            className={`px-5 py-2 rounded-lg text-sm font-semibold transition-all ${activeTab === tab.id ? 'bg-white shadow-sm text-blue-700' : 'text-gray-500 hover:text-gray-700'}`}>
            {tab.label}
          </button>
        ))}
      </div>
      {activeTab === 'invitations' && <InvitationsPanel campaign={campaign} api={api} onBack={onBack} hideHeader />}
      {activeTab === 'maya' && <MayaPanel campaign={campaign} api={api} />}
    </div>
  );
}

// ─── Main Component ───────────────────────────────────────────────────────────

export default function LinkedInCampaigns({ api }) {
  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCampaign, setEditingCampaign] = useState(null);
  const [openCampaign, setOpenCampaign] = useState(null);
  const [runningId, setRunningId] = useState(null);
  const [runResult, setRunResult] = useState(null);
  const [runResultOpen, setRunResultOpen] = useState(false);

  const fetchCampaigns = async () => {
    try {
      const res = await api.get('/linkedin-campaigns');
      setCampaigns(res.data || []);
    } catch { toast.error('Erro ao carregar campanhas'); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchCampaigns(); }, []);

  const handleRun = async (campaign) => {
    setRunningId(campaign.id);
    try {
      const res = await api.post(`/linkedin-campaigns/${campaign.id}/run`);
      setRunResult(res.data);
      setRunResultOpen(true);
      fetchCampaigns();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erro ao executar campanha');
    } finally { setRunningId(null); }
  };

  const handlePause = async (campaign) => {
    try {
      await api.post(`/linkedin-campaigns/${campaign.id}/pause`);
      toast.success('Campanha pausada');
      fetchCampaigns();
    } catch { toast.error('Erro ao pausar'); }
  };

  const handleDelete = async (campaign) => {
    if (!window.confirm(`Remover campanha "${campaign.name}"? Todos os convites registrados serão apagados.`)) return;
    try {
      await api.delete(`/linkedin-campaigns/${campaign.id}`);
      toast.success('Campanha removida');
      fetchCampaigns();
    } catch { toast.error('Erro ao remover'); }
  };

  const handleEdit = (campaign) => {
    setEditingCampaign(campaign);
    setShowForm(true);
  };

  if (openCampaign) {
    const current = campaigns.find(c => c.id === openCampaign.id) || openCampaign;
    return (
      <CampaignDetail
        campaign={current}
        api={api}
        onBack={() => { setOpenCampaign(null); fetchCampaigns(); }}
      />
    );
  }

  const totalStats = campaigns.reduce((acc, c) => ({
    campaigns: acc.campaigns + 1,
    found: acc.found + (c.total_found || 0),
    invited: acc.invited + (c.total_invited || 0),
    accepted: acc.accepted + (c.total_accepted || 0),
  }), { campaigns: 0, found: 0, invited: 0, accepted: 0 });

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">LinkedIn</h1>
          <p className="text-gray-500 mt-1 text-sm">Campanhas de prospecção automatizadas via Synkly</p>
        </div>
        <Button onClick={() => { setEditingCampaign(null); setShowForm(true); }} className="bg-blue-600 hover:bg-blue-700 text-white">
          + Nova campanha
        </Button>
      </div>

      {/* Global stats */}
      {campaigns.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <StatBox label="Campanhas" value={totalStats.campaigns} color="gray" />
          <StatBox label="Perfis encontrados" value={totalStats.found} color="blue" />
          <StatBox label="Convites enviados" value={totalStats.invited} color="blue" />
          <StatBox label="Aceitos" value={totalStats.accepted} color="green" />
        </div>
      )}

      {/* Campaign list */}
      {loading ? (
        <div className="text-center py-16 text-gray-400">
          <span className="animate-spin inline-block mr-2 text-xl">⏳</span> Carregando campanhas…
        </div>
      ) : campaigns.length === 0 ? (
        <Card>
          <CardContent className="p-16 text-center">
            <div className="text-6xl mb-4">🔵</div>
            <h3 className="text-lg font-semibold text-gray-800">Nenhuma campanha criada</h3>
            <p className="text-gray-500 mt-2 max-w-sm mx-auto text-sm">
              Crie sua primeira campanha para buscar perfis no LinkedIn e enviar convites automaticamente via Synkly.
            </p>
            <Button className="mt-6 bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowForm(true)}>
              + Criar primeira campanha
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="space-y-3">
          {campaigns.map(c => (
            <CampaignCard
              key={c.id}
              campaign={c}
              onOpen={setOpenCampaign}
              onRun={handleRun}
              onPause={handlePause}
              onDelete={handleDelete}
              onEdit={handleEdit}
              running={runningId}
            />
          ))}
        </div>
      )}


      <CampaignFormModal
        open={showForm}
        onClose={() => { setShowForm(false); setEditingCampaign(null); }}
        onSaved={fetchCampaigns}
        api={api}
        editing={editingCampaign}
      />

      <RunResultModal result={runResult} open={runResultOpen} onClose={() => setRunResultOpen(false)} />
    </div>
  );
}
